/**
 * Librarian Task Management Hooks
 * Provides React Query hooks for managing librarian daily tasks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { librarianTaskApi, LibrarianTask, CreateTaskData, UpdateTaskData, TaskQueryParams } from '@/services/librarian-tasks-api';
import { toast } from 'sonner';

/**
 * Query key factories for task management
 */
export const taskQueryKeys = {
  all: ['librarian-tasks'] as const,
  lists: () => [...taskQueryKeys.all, 'list'] as const,
  list: (params?: TaskQueryParams) => [...taskQueryKeys.lists(), params] as const,
  details: () => [...taskQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...taskQueryKeys.details(), id] as const,
} as const;

/**
 * Hook to get list of tasks
 */
export const useLibrarianTasks = (params?: TaskQueryParams) => {
  return useQuery({
    queryKey: taskQueryKeys.list(params),
    queryFn: () => librarianTaskApi.getTasks(params),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  });
};

/**
 * Hook to get a specific task
 */
export const useLibrarianTask = (taskId: number) => {
  return useQuery({
    queryKey: taskQueryKeys.detail(taskId),
    queryFn: () => librarianTaskApi.getTask(taskId),
    enabled: !!taskId,
  });
};

/**
 * Hook to create a new task
 */
export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTaskData) => librarianTaskApi.createTask(data),
    onSuccess: () => {
      // Invalidate and refetch task lists
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      toast.success('Task created successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create task');
    },
  });
};

/**
 * Hook to update a task
 */
export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: number; data: UpdateTaskData }) => 
      librarianTaskApi.updateTask(taskId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific task and list
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.detail(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      toast.success('Task updated successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update task');
    },
  });
};

/**
 * Hook to delete a task
 */
export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: number) => librarianTaskApi.deleteTask(taskId),
    onSuccess: () => {
      // Invalidate task lists
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      toast.success('Task deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete task');
    },
  });
};

/**
 * Hook to complete a task
 */
export const useCompleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: number) => librarianTaskApi.completeTask(taskId),
    onMutate: async (taskId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: taskQueryKeys.lists() });

      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData(taskQueryKeys.lists());

      // Optimistically update to the new value
      queryClient.setQueriesData({ queryKey: taskQueryKeys.lists() }, (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((task: LibrarianTask) =>
            task.id === taskId ? { ...task, completed: true } : task
          ),
        };
      });

      // Return context with the snapshot
      return { previousTasks };
    },
    onSuccess: (_, taskId) => {
      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.detail(taskId) });
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      toast.success('Task completed!');
    },
    onError: (error: any, _, context) => {
      // Rollback optimistic update on error
      if (context?.previousTasks) {
        queryClient.setQueriesData({ queryKey: taskQueryKeys.lists() }, context.previousTasks);
      }
      toast.error(error?.response?.data?.message || 'Failed to complete task');
    },
  });
};

/**
 * Hook to get today's tasks (incomplete only)
 */
export const useTodayTasks = () => {
  return useLibrarianTasks({ completed: false });
};

/**
 * Hook to get completed tasks
 */
export const useCompletedTasks = () => {
  return useLibrarianTasks({ completed: true });
};

/**
 * Hook for bulk task completion
 */
export const useBulkCompleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskIds: number[]) => {
      // Complete tasks one by one (could be optimized with a bulk API endpoint)
      const results = await Promise.all(
        taskIds.map(id => librarianTaskApi.completeTask(id))
      );
      return results;
    },
    onSuccess: (_, taskIds) => {
      // Invalidate task lists
      queryClient.invalidateQueries({ queryKey: taskQueryKeys.lists() });
      toast.success(`${taskIds.length} tasks completed successfully`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to complete tasks');
    },
  });
};

/**
 * Hook to get task statistics
 */
export const useTaskStatistics = () => {
  return useQuery({
    queryKey: [...taskQueryKeys.all, 'statistics'],
    queryFn: async () => {
      // Get all tasks to calculate statistics
      const allTasks = await librarianTaskApi.getTasks();
      const tasks = allTasks.results || [];
      
      const total = tasks.length;
      const completed = tasks.filter(t => t.completed).length;
      const pending = total - completed;
      const urgent = tasks.filter(t => t.priority === 'urgent' && !t.completed).length;
      const overdue = tasks.filter(t => {
        if (t.completed || !t.due_time) return false;
        return new Date(t.due_time) < new Date();
      }).length;

      // Calculate completion rate by category
      const byCategory = tasks.reduce((acc, task) => {
        if (!acc[task.category]) {
          acc[task.category] = { total: 0, completed: 0 };
        }
        acc[task.category].total++;
        if (task.completed) {
          acc[task.category].completed++;
        }
        return acc;
      }, {} as Record<string, { total: number; completed: number }>);

      // Calculate completion rate by priority
      const byPriority = tasks.reduce((acc, task) => {
        if (!acc[task.priority]) {
          acc[task.priority] = { total: 0, completed: 0 };
        }
        acc[task.priority].total++;
        if (task.completed) {
          acc[task.priority].completed++;
        }
        return acc;
      }, {} as Record<string, { total: number; completed: number }>);

      return {
        total,
        completed,
        pending,
        urgent,
        overdue,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        byCategory,
        byPriority,
        autoGenerated: tasks.filter(t => t.auto_generated).length,
        manualTasks: tasks.filter(t => !t.auto_generated).length
      };
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 2 * 60 * 1000, // Auto-refresh every 2 minutes
  });
};
